<template>
<div style="min-height:200px;">
  <el-row type="flex" class="title-bar" :style="'justify-content:' + (operationFlag ? 'flex-end;' : 'space-between;')">
    <w-perf-analysis-time-info v-if="!operationFlag" :ref="timeInfoRef" :checkDatetime="checkDatetime" @change="onTimeTypeChange" @reload="onCurrentDataReLoad" />
    <div>
      <el-radio-group class="mr-r10" style="display:inline-block;" v-model="processType">
        <el-radio label="all">전체</el-radio>
        <el-radio label="zombie">좀비 프로세스</el-radio>
      </el-radio-group>
      <w-oligopoly-popover-button style="display:inline-block;" @update="loadData" />
      <w-header-search-button @click="onSearchToolButtonClick"></w-header-search-button>
    </div>
  </el-row>
  <el-row>
    <el-table-wrapper v-loading="loading" :data="filteredTableData" :columns="columns" :column-default="columnDefault" :row-class-name="getProcessRowClass" :show-custom-header="true" :show-search-tool="showSearchTool" :show-overflow-tooltip="true" :pagination="true">
      <template slot-scope="scope" slot="cpu-slot">
        <el-progress v-if="scope.row.procCpuUserate > 0" :class="getCpuProgressbarClass(scope.row.procCpuUserate)" :text-inside="true" :stroke-width="18" :percentage="scope.row.procCpuUserate"></el-progress>
        <el-progress v-else :text-inside="true" :stroke-width="18" :percentage="0"></el-progress>
      </template>
      <template slot-scope="scope" slot="memory-slot">
        <span :class="getMemValueClass(scope.row.procMemUsedsize)">{{getConvertValuByUnit(scope.row.procMemUsedsize)}}</span>
      </template>
    </el-table-wrapper>
  </el-row>
</div>
</template>

<script>
import { isNil, isEmpty, concat, forEach, filter } from 'lodash';
import { analysisTimeType } from '@/constants/performance';
import { columnDefault } from '@/constants/table';
import msgUtil from '@/utils/message';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { convertValuByUnit } from '@/utils/number';
import serverTableInfoAPI from '@/api/performance/server/info-table';
import analysisTableMixin from '@/mixins/performance/analysis-table-ex';
import WPerfAnalysisTimeInfo from '@/views/performance/node/analysis/components/PerfAnalysisTimeInfo';
import WOligopolyPopoverButton from '../button/OligopolyPopoverButton';

export default {
  name: 'WProcessTable',
  mixins: [analysisTableMixin],
  components: {
    WPerfAnalysisTimeInfo,
    WOligopolyPopoverButton,
  },
  data() {
    return {
      showSearchTool: false,
      loading: false,
      processType: 'all',
      checkDatetime: null,
      columnDefault,
      tableData: [],
      columns: [
        {
          prop: 'statusAlive',
          label: '동작상태',
          formatter: this.formatterForStatusAlive,
          width: 100,
        },
        {
          prop: 'procUid',
          label: '소유자',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
          width: 120,
          sortable: true,
        },
        {
          prop: 'procPid',
          label: 'PID',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
          width: 100,
          sortable: true,
        },
        {
          prop: 'procPpid',
          label: 'PPID',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
          width: 100,
          sortable: true,
        },
        {
          prop: 'procName',
          label: '프로세스명',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
          sortable: true,
        },
        {
          prop: 'procCpuUserate',
          label: 'CPU (%)',
          align: 'center',
          scopedSlot: 'cpu-slot',
          width: 150,
          sortable: true,
        },
        {
          prop: 'procMemUsedsize',
          label: '메모리',
          scopedSlot: 'memory-slot',
          width: 120,
          sortable: true,
        },
        {
          prop: 'procCputime',
          label: 'CPU 시간',
          formatter: this.getTableValueAfterCheckEmpty,
          width: 130,
          sortable: true,
        },
        {
          prop: 'proPri',
          label: '우선순위',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
          width: 100,
          sortable: true,
        },
      ],
    };
  },
  computed: {
    /**
     * 프로세스 타입에 따라 프로세스 정보를 필터링하여 반환한다.
     * @author Ju
     * @return {[type]} [description]
     */
    filteredTableData() {
      if (this.processType === 'zombie') {
        return filter(this.tableData, (item) => item.procName.indexOf('defunct') >= 0);
      }
      return this.tableData;
    },
  },
  methods: {
    /**
     * 프로세스 데이터를 조회한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadData() {
      // 과점유 설정 로드
      this.loadProcessCpuMemLimitSetting();

      // 프로세스 정보 로드
      const params = {
        objId: this.selectedNode.obj.objId,
      };
      if (this.timeType === analysisTimeType.CURRENT.value || this.operationFlag) {
        params.conditionId = 'procTotalCount';
        this.loading = true;
        serverTableInfoAPI
          .getCurrentProcessInfoList(params)
          .then((response) => {
            if (response.data.success === true) {
              this.setTableData(response.data.data);
            } else {
              this.failToLoad();
            }
          })
          .catch(() => {
            this.failToLoad();
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        params.baselineDatetime = this.plotLineDatetime;
        params.managetypeId = 'SMS';
        serverTableInfoAPI
          .getSmsProcessInfoList(params)
          .then((response) => {
            if (response.data.success === true) {
              const result = response.data.data;
              this.checkDatetime = result.timestamp;
              this.setTableData(result.snapshotDoc);
            } else {
              this.failToLoad();
            }
          })
          .catch(() => {
            this.failToLoad();
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    /**
     * 데이터 로드 실패 처리
     * @author Ju
     * @return {[type]} [description]
     */
    failToLoad() {
      this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '프로세스 정보', false));
    },
    /**
     * 프로세스 CPU / 메모리 과점유 설정 정보 조회
     * @author Ju
     * @return {[type]} [description]
     */
    loadProcessCpuMemLimitSetting() {
      serverTableInfoAPI
        .getProcessOverloadSettings(this.selectedNode.obj.objId)
        .then((response) => {
          if (response.data.success === true) {
            const result = response.data.data;
            this.maxCpuLimit = result.processMaxCpuLimit;
            this.maxMemLimit = result.processMaxMemLimit * 1024;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '과점유 설정 정보', false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '과점유 설정 정보', false));
        });
    },
    /**
     * 프로세스 테이블 데이터를 설정한다.
     * @author Ju
     * @param  {[type]} data [description]
     */
    setTableData(data) {
      if (isEmpty(data)) {
        this.tableData = [];
      } else {
        forEach(data.major, (item) => {
          item.majorFlag = true;
        });
        this.tableData = concat(data.major, data.process);
      }
    },
    /**
     * [getProcessRowClass table row에 major 데이터 색상삽입]
     * @author {Yang}
     * @param  {[type]} row      [description]
     * @param  {[type]} rowIndex [description]
     * @return {[String]}        [major data insert bgcolor]
     */
    getProcessRowClass({ row }) {
      if (row.majorFlag) {
        return 'point-import';
      }
      return '';
    },
    /**
     * [getCpuProgressbarClass cpu의 limit 판단 메서드/ 프로그래스바 색깔 변경]
     * @author {Yang}
     * @param  {[type]} procCpuUserate [cpu 값]
     * @return {[type]}                [color]
     */
    getCpuProgressbarClass(value) {
      if (!isNil(this.maxCpuLimit) && value >= this.maxCpuLimit) {
        return 'abnormal';
      }
      return null;
    },
    /**
     * [getCpuProgressbarClass momory의 limit 판단 메서드/ 글씨 색깔 변경]
     * @author {Yang}
     * @param  {[type]} procCpuUserate [momory 값]
     * @return {[type]}                [color]
     */
    getMemValueClass(value) {
      if (!isNil(this.maxMemLimit) && value > this.maxMemLimit) {
        return 'state-abnormal';
      }
      return null;
    },
    onSearchToolButtonClick() {
      this.showSearchTool = !this.showSearchTool;
    },
    formatterForStatusAlive(row) {
      const statusAlive = row.statusAlive;
      let value;
      if (statusAlive === 0 || statusAlive === '0') {
        value = '다운 상태';
      } else if (statusAlive === 1 || statusAlive === '1') {
        value = '정상 동작';
      } else {
        value = EMPTY_TEXT;
      }
      return value;
    },
    /**
     * 테이블 데이터 빈값 확인
     * @author Yang
     * @param  {Object} row
     * @param  {Object} column
     * @param  {String/Number} valu
     */
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * [getConvertValuByUnit 자리수 변환]
     * @author {Yang}
     */
    getConvertValuByUnit(value) {
      return convertValuByUnit(value, 'KB', true, false, true);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/server/components/table/ProcessTable.vue